import React, { useContext, useEffect, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useQuery } from '@apollo/client/react';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { ActionsGridParent, RiskMonitoringTab } from '@pages-clean/risk-assessment/constants';
import { ExtendedRiskObservationFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { RiskMonitoringContext } from '@pages-clean/risk-assessment/risk-monitoring/interfaces/context';
import SharedActionsGrid from '@pages-clean/risk-assessment/shared/actions/actions.grid';
import { Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { CompleteRiskFields } from '@queries/generated/fragments.generated';
import {
    riskObservationsRisks,
    riskObservationsRisksVariables,
} from '@queries/generated/legacy-risk-assessment.generated';
import { GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS } from '@queries/risk-assessment';

const ManageActionsGrid = () => {
    const user = useAuthorization();
    const canView = user?.canRead?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.Read,
    });

    if (!canView) return null;
    const { current, setCurrent } = useContext(RiskMonitoringContext);
    const { showDeleted: curShowDeleted } = current;
    const studyId = current.study?.id;
    const [observations, setObservations] = useState<ExtendedRiskObservationFields[]>();
    const [risks, setRisks] = useState<CompleteRiskFields[]>();
    const showDeleted = useBoolean(curShowDeleted);
    const [selectedObservations, setSelectedObservations] = useState<{
        observationIds: string[];
        selectedActionCount: number;
    }>({
        observationIds: [],
        selectedActionCount: 0,
    });

    const { error, data } = useQuery<riskObservationsRisks, riskObservationsRisksVariables>(
        GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS,
        {
            variables: { studyId },
        },
    );

    useEffect(() => {
        if (selectedObservations && observations && risks) {
            if (selectedObservations.observationIds?.length === 1) {
                const observation = observations.find((ob) => ob.id === selectedObservations.observationIds![0]);
                const risk: CompleteRiskFields | undefined = risks.find((ri) => ri.id === observation?.riskId);

                setCurrent((prevState) => ({
                    ...prevState,
                    selectedObservation: observation,
                    selectedObservationRisk: risk,
                    observationCount: 1,
                    actionCount: selectedObservations.selectedActionCount,
                }));
            } else
                setCurrent((prevState) => ({
                    ...prevState,
                    selectedObservation: undefined,
                    selectedObservationRisk: undefined,
                    observationCount: selectedObservations.observationIds.length,
                    actionCount: selectedObservations.selectedActionCount,
                }));
        }
    }, [selectedObservations.observationIds]);

    useEffect(() => {
        if (current.showDeleted !== showDeleted.value)
            setCurrent((prevState) => ({
                ...prevState,
                showDeleted: showDeleted.value,
            }));
    }, [showDeleted.value]);

    useEffect(() => {
        if (data && data.risks && data.riskObservations) {
            const riskIds = data.risks.map((r) => r?.id);
            const observationList = (
                showDeleted.value ? data.riskObservations : data.riskObservations.filter((ob) => !ob?.deleted)
            )
                .filter((ob) => riskIds.includes(ob?.riskId))
                .map((item) => {
                    return {
                        ...item,
                        riskRecordId: data.risks?.find((risk) => risk?.id === item?.riskId)?.recordId,
                        numActions:
                            (item?.actions &&
                                item.actions.length &&
                                item.actions.filter((action) => action && !action.deleted).length) ??
                            0,
                        numOpenActions:
                            (item?.actions &&
                                item.actions.length &&
                                item.actions.filter((action) => action && !action.deleted && !action.completedDate)
                                    .length) ??
                            0,
                        ...(item?.deleted && item?.audit
                            ? {
                                  reasonForDeletion: item.audit.reason,
                                  deletedDate: item.audit.created,
                                  deletedBy: `${item.audit.userFirstName} ${item.audit.userLastName}`,
                              }
                            : {}),
                        recentActionDate:
                            item?.actions &&
                            item.actions
                                .filter((activeActions) => activeActions && !activeActions.deleted)
                                .map((action) => action && action.requestDate)
                                .sort()
                                .reverse()[0],
                        convertedOwner: item?.owner
                            ? {
                                  name: `${item.owner.firstName} ${item.owner.lastName}`,
                                  active: item.owner.active,
                              }
                            : {
                                  name: '',
                                  active: true,
                              },
                        createdBy: item?.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : '',
                        updatedBy: item?.updatedBy ? `${item.updatedBy.firstName} ${item.updatedBy.lastName}` : '',
                    };
                });
            const riskList = data.risks.filter((ri) => !!observationList.find((ob) => ri?.id === ob.riskId));

            setObservations(observationList as unknown[] as ExtendedRiskObservationFields[]);
            setRisks(riskList as unknown[] as CompleteRiskFields[]);

            if (current.selectedObservation) {
                const updatedObservation = observationList.find((ob) => ob.id === current.selectedObservation?.id);
                setCurrent((prevState) => ({
                    ...prevState,
                    selectedObservation: updatedObservation as unknown as ExtendedRiskObservationFields,
                }));
            }
        }
    }, [data, showDeleted]);

    if (error) return <ErrorMessage />;

    if (!data) return <Loading />;

    return (
        <SharedActionsGrid
            selectedAction={undefined}
            showDeleted={showDeleted}
            studyId={current.study?.id}
            studyNumber={current.study?.studyNumber}
            observations={observations ?? []}
            risks={(risks ?? []) as unknown[] as CompleteRiskFields[]}
            parent={ActionsGridParent.RiskMonitoring}
            setObservationIds={setSelectedObservations}
            tab={RiskMonitoringTab.ManageActions}
        />
    );
};

export default ManageActionsGrid;
